import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Link2, Unplug, AlertTriangle, Loader2 } from "lucide-react";

const ago = (iso) => {
  if (!iso) return "never";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Xero connection status + Connect / Sync now / last sync time, shown on Finance.
export function XeroSyncBar({ isAdmin, canSync, onSynced }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/xero/connection", { params: { origin: window.location.origin } }).then((r) => setS(r.data)).catch(() => setS(null));

  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    if (p.get("xero") === "connected") toast.success("Xero connected — click Sync now to bring in invoices.");
    if (p.get("xero") === "error") toast.error(`Xero connection failed (${p.get("reason") || "unknown"}). Please try again.`);
    if (p.get("xero")) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  async function connect() {
    try { const r = await api.get("/xero/connect", { params: { origin: window.location.origin } }); window.location.href = r.data.url; }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  async function sync() {
    setBusy(true);
    try {
      const r = await api.post("/xero/sync");
      toast.success(`Xero synced · ${r.data.invoices_updated} invoices updated, ${r.data.new_invoices} new${r.data.auto_linked ? `, ${r.data.auto_linked} clients auto-linked` : ""}${r.data.gc_customers != null ? ` · GoCardless ${r.data.gc_customers} customers` : ""}`);
      if (r.data.gc_error) toast.error(`GoCardless sync failed: ${r.data.gc_error}`);
      await load(); onSynced?.();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); load(); }
    setBusy(false);
  }
  async function disconnect() {
    if (!window.confirm("Disconnect Xero? Existing invoice data stays, but Sync now will stop working until you reconnect.")) return;
    await api.post("/xero/disconnect"); toast.success("Xero disconnected"); load();
  }

  if (!s) return null;
  if (!s.connected || s.needs_reconnect) {
    return (
      <div className="flex items-center gap-2 text-[11px]" data-testid="xero-bar">
        {s.needs_reconnect && <span className="flex items-center gap-1 text-[var(--err)]"><AlertTriangle size={12} /> Xero connection expired</span>}
        {!s.configured && <span className="text-[var(--ink-2)]" data-testid="xero-not-configured">Xero not set up yet</span>}
        {isAdmin && s.configured && (
          <span className="text-[10px] text-[var(--ink-2)] font-mono-data" data-testid="xero-redirect-uri" title="This exact address must be listed under Redirect URIs in your Xero app">Xero redirect: {s.redirect_uri}</span>
        )}
        {isAdmin && s.configured && (
          <button data-testid="xero-connect-btn" onClick={connect} className="btn-primary flex items-center gap-1.5 text-[11px] !py-1.5 !px-3">
            <Link2 size={12} /> {s.needs_reconnect ? "Reconnect Xero" : "Connect with Xero"}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 text-[11px]" data-testid="xero-bar">
      <div className="text-right leading-tight">
        <div className="text-[var(--ink)]" data-testid="xero-tenant"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--ok)] mr-1.5 align-middle" />Xero · {s.tenant_name}</div>
        <div className="text-[var(--ink-2)]" data-testid="xero-last-sync">Last sync: {ago(s.last_sync_at)}{s.last_sync_by ? ` by ${s.last_sync_by}` : ""}</div>
        {s.last_sync_error && <div className="text-[var(--err)]" data-testid="xero-last-error">Last attempt failed: {s.last_sync_error}</div>}
      </div>
      {canSync && (
        <button data-testid="xero-sync-btn" onClick={sync} disabled={busy} className="btn-primary flex items-center gap-1.5 text-[11px] !py-1.5 !px-3">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {busy ? "Syncing…" : "Sync now"}
        </button>
      )}
      {isAdmin && (
        <button data-testid="xero-disconnect-btn" onClick={disconnect} title="Disconnect Xero" className="btn-ghost !py-1.5 !px-2"><Unplug size={12} /></button>
      )}
    </div>
  );
}
